import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, LogOut, PanelLeftClose, Smartphone } from 'lucide-react'
import { api } from '../lib/api'
import { useAppearance } from '../lib/appearance'
import { broadcastAuthChange } from '../lib/auth-broadcast'
import { getMailOutbox } from '../lib/mail-outbox'
import type { NavItemDef } from '../lib/nav'
import { visibleGroups } from '../lib/nav'
import { notify } from '../lib/notify'
import { onlineStatus } from '../lib/online-status'
import { duringLogout, endSession } from '../lib/session-end'
import { cn } from '../lib/utils'
import { BrandLogo } from './brand-logo'
import { NotificationBell } from './notification-center'
import { usePwaInstallPlatform } from './pwa-install-sheet'
import { Button } from './ui/button'

interface SidebarProps {
  collapsed: boolean
  onToggleCollapsed: () => void
  onItemClick?: () => void
  onNotifClick?: () => void
  /** Öffnet das PWA-Install-Sheet (nur relevant im Mobile-Drawer). */
  onInstallClick?: () => void
  /** Schließt den Mobile-Drawer — wenn gesetzt, erscheint der Schließen-
   *  Toggle unten links neben der User-Anzeige (Daumen-Toggle, spiegelt das
   *  Menü-Icon der Bottom-Bar). Desktop-Sidebar setzt das nie. */
  onClose?: () => void
  /** Versteckt den „einklappen"-Toggle. Gesetzt vom Tablet-Band-Rail
   *  (collapsed ist dort ERZWUNGEN, ein Toggle wäre eine Lüge) und vom
   *  Drawer (dessen onToggleCollapsed ist ein No-op). Als Rail-Marker
   *  schaltet das Prop zusätzlich die Footer-Bell im collapsed-Modus frei —
   *  das Band hat keine Topbar mehr, die Rail-Bell ist dort der einzige
   *  Notification-Zugang. Desktop ≥1024 (Prop nie gesetzt) bleibt
   *  byte-identisch: collapsed ohne Bell, Toggle sichtbar. */
  hideCollapseToggle?: boolean
}

export function Sidebar({
  collapsed,
  onToggleCollapsed,
  onItemClick,
  onNotifClick,
  onInstallClick,
  onClose,
  hideCollapseToggle,
}: SidebarProps) {
  const { data: me } = useQuery({ queryKey: ['auth', 'me'], queryFn: () => api.me() })
  const location = useRouterState({ select: (s) => s.location.pathname })

  const logoutMutation = useMutation({
    mutationFn: () =>
      // A 401 during the flush waits for this logout (#834): no parallel purge
      // before the outbox is counted.
      duringLogout(
        (async () => {
          // Mail outbox (#785): send what is still waiting (bounded), then count
          // what would be lost; the whole offline database goes after the logout.
          const userId = me?.user?.id ?? null
          const discarded = userId ? await settleMailOutbox(userId) : 0
          const res = await api.logout()
          return { res, userId, discarded }
        })(),
      ),
    onSuccess: async ({ res, userId, discarded }) => {
      // Central session end (#834): running queries cancelled, cache emptied
      // so mounted pages see me = null (clearing the whole client left them
      // rendering the old mails), stores cleared, the offline database of the
      // user purged, then replace to /login.
      const ended = endSession('logout', userId)
      broadcastAuthChange('logout')
      if (res.autoStoppedEntryId) {
        notify.warning('Laufender Timer wurde beim Abmelden gestoppt.')
      } else {
        notify.success('Abgemeldet')
      }
      await ended
      if (discarded > 0) {
        notify.warning(
          `${discarded} ${discarded === 1 ? 'Mail' : 'Mails'} aus dem Ausgang verworfen`,
        )
      }
    },
    onError: async (err) => {
      // Session was already gone (401): same clean-up and /login.
      if ((err as { status?: number }).status !== 401) return
      const ended = endSession('logout', me?.user?.id ?? null)
      broadcastAuthChange('logout')
      notify.success('Abgemeldet')
      await ended
    },
  })

  // Pending signatures count for inbox-badge — refetched every 30s
  const { data: incomingData } = useQuery({
    queryKey: ['documents', { incoming: true }],
    queryFn: () => api.listDocuments({ incoming: true }),
    enabled: !!me?.user,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })
  const pendingInboxCount =
    incomingData?.signatures.filter((s) => s.status === 'pending').length ?? 0

  // Total unread across all chats — drives the badge on the Chat entry.
  // Same refetch cadence as the chat list itself so the two stay in sync.
  const { data: chatsData } = useQuery({
    queryKey: ['chats'],
    queryFn: () => api.listChats(),
    enabled: !!me?.user,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  })
  const chatUnreadTotal = chatsData?.chats.reduce((s, c) => s + c.unread, 0) ?? 0

  const badges: Partial<Record<string, number>> = {
    documents: pendingInboxCount,
    chat: chatUnreadTotal,
  }

  const groups = visibleGroups(me?.user ?? null)
  const allTos = groups.flatMap((g) => g.items.map((i) => i.to))
  const brandLabel = useAppearance((s) => s.brandLabel)
  // Dauerhafter Install-Menüpunkt: nur wenn die Umgebung installierbar ist
  // (Phone im Browser-Tab) — Desktop und standalone sehen ihn nie.
  const installPlatform = usePwaInstallPlatform()

  return (
    <div className="flex h-full flex-col">
      {/* Brand — links to user's configured start page or dashboard */}
      <Link
        to={me?.user?.startPage || '/'}
        onClick={onItemClick}
        className={cn(
          'flex items-center border-b border-border-subtle py-5 transition-all',
          collapsed ? 'justify-center px-3' : 'gap-2.5 px-6',
        )}
      >
        <BrandLogo size={20} />
        {!collapsed && (
          <span className="brand-mark text-xl">
            {location.endsWith('/settings/invitations') ? 'DarkEmber' : brandLabel}
          </span>
        )}
      </Link>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto overscroll-contain p-3 space-y-5 scrollbar-thin">
        {groups.map((g) => (
          <div key={g.id}>
            {!collapsed && g.label && (
              <p className="px-2 pb-1.5 text-2xs uppercase tracking-[0.14em] text-text-tertiary font-medium">
                {g.label}
              </p>
            )}
            <div className="space-y-0.5">
              {g.items.map((item) => (
                <NavLink
                  key={item.key}
                  item={item}
                  active={isActive(location, item.to, allTos)}
                  collapsed={collapsed}
                  onClick={onItemClick}
                  badge={badges[item.key]}
                />
              ))}
            </div>
          </div>
        ))}

        {/* „Als App installieren" — öffnet dasselbe Sheet wie die
            Auto-Anzeige, ignoriert dabei bewusst den Dismiss-Snooze. */}
        {installPlatform !== 'none' && onInstallClick && (
          <div className="border-t border-border-subtle pt-3">
            <button
              type="button"
              onClick={onInstallClick}
              className="group flex w-full items-center gap-3 rounded-lg px-3 py-2 touch:min-h-11 text-sm text-text-secondary transition-all hover:bg-bg-overlay hover:text-text-primary"
            >
              <Smartphone className="h-4 w-4 shrink-0" strokeWidth={2} />
              <span className="flex-1 truncate text-left">Als App installieren</span>
            </button>
          </div>
        )}
      </nav>

      {/* User footer + Collapse toggle */}
      {me?.user && (
        <div className="border-t border-border-subtle p-3 space-y-1">
          {/* Rail-Bell (Tablet-Band): eigene Zeile über dem Avatar — die
              expandierte Bell sitzt IN der User-Zeile, collapsed passt dort
              nur der Avatar in die 56px-Spalte. Unread-Badge inklusive. */}
          {collapsed && hideCollapseToggle && onNotifClick && (
            <div className="flex justify-center">
              <NotificationBell onClick={onNotifClick} />
            </div>
          )}
          <div
            className={cn(
              'flex items-center rounded-lg px-3 py-2.5 touch:min-h-11',
              collapsed && 'justify-center px-1',
            )}
          >
            {/* Drawer-Schließen als 1. Element links neben der User-Anzeige —
                gleiche Daumen-Position wie das Menü-Icon der Bottom-Bar zum
                Öffnen (bequemes Auf/Zu-Toggle). Nur im Mobile-Drawer. */}
            {onClose && !collapsed && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Seitenleiste schließen"
                className="mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-tertiary hover:bg-bg-overlay hover:text-text-primary"
              >
                <PanelLeftClose className="h-5 w-5" />
              </button>
            )}
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent text-xs font-semibold">
              {me.user.name.slice(0, 2).toUpperCase()}
            </div>
            {!collapsed && (
              <>
                <div className="ml-3 min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-text-primary">{me.user.name}</p>
                  <p className="truncate text-xs text-text-tertiary capitalize">
                    {me.user.roleObj?.name ?? me.user.role}
                  </p>
                </div>
                {onNotifClick && <NotificationBell onClick={onNotifClick} />}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => logoutMutation.mutate()}
                  aria-label="Abmelden"
                  className="ml-1 h-7 w-7 text-text-tertiary hover:text-accent"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
          {!hideCollapseToggle && (
            <button
              type="button"
              onClick={onToggleCollapsed}
              aria-label={collapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen'}
              className={cn(
                'hidden md:flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-xs text-text-tertiary',
                'hover:bg-bg-overlay hover:text-text-primary transition-colors',
                collapsed && 'justify-center px-1',
              )}
            >
              {collapsed ? (
                <ChevronRight className="h-3.5 w-3.5" />
              ) : (
                <ChevronLeft className="h-3.5 w-3.5" />
              )}
              {!collapsed && <span>einklappen</span>}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function NavLink({
  item,
  active,
  collapsed,
  onClick,
  badge,
}: {
  item: NavItemDef
  active: boolean
  collapsed: boolean
  onClick?: () => void
  badge?: number
}) {
  const Icon = item.icon
  const showBadge = typeof badge === 'number' && badge > 0
  return (
    <Link
      to={item.to}
      onClick={onClick}
      title={collapsed ? `${item.label}${showBadge ? ` (${badge})` : ''}` : undefined}
      className={cn(
        'group relative flex items-center rounded-lg text-sm transition-all touch:min-h-11',
        collapsed ? 'justify-center px-2.5 py-2.5' : 'gap-3 px-3 py-2',
        active
          ? 'bg-accent/10 text-accent'
          : 'text-text-secondary hover:bg-bg-overlay hover:text-text-primary',
      )}
    >
      {/* Active indicator bar */}
      {active && (
        <span
          aria-hidden
          className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-r-full bg-accent"
        />
      )}
      <span className="relative shrink-0">
        <Icon className="h-4 w-4" strokeWidth={active ? 2.25 : 2} />
        {/* Collapsed: small dot in corner of icon */}
        {showBadge && collapsed && (
          <span
            aria-hidden
            className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-accent ring-2 ring-bg-elevated"
          />
        )}
      </span>
      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
      {/* Expanded: numeric pill */}
      {showBadge && !collapsed && (
        <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-accent px-1.5 py-0.5 text-2xs font-semibold leading-none text-bg-base tabular-nums">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  )
}

// Personalverwaltung occupies a fixed set of /settings/* paths even though
// it lives in its own sidebar entry. Keep the list here so isActive can
// route lighting between Personalverwaltung and Einstellungen.
const PERSONNEL_PATHS = new Set(['/settings/teams', '/settings/users', '/settings/surcharges'])

function isActive(currentPath: string, to: string, allTos: string[]): boolean {
  if (to === '/') return currentPath === '/'
  if (to === '/settings/teams') {
    return PERSONNEL_PATHS.has(currentPath)
  }
  if (to === '/settings/profile') {
    // Settings = anything under /settings that isn't Personalverwaltung
    return currentPath.startsWith('/settings') && !PERSONNEL_PATHS.has(currentPath)
  }
  if (currentPath === to) return true
  if (!currentPath.startsWith(`${to}/`)) return false
  // Sub-path match — but defer to a more specific sibling if one exists
  // (e.g. /absences/team has its own entry; /absences must NOT light up).
  const moreSpecific = allTos.some(
    (other) =>
      other !== to &&
      other.startsWith(`${to}/`) &&
      (currentPath === other || currentPath.startsWith(`${other}/`)),
  )
  return !moreSpecific
}

const LOGOUT_FLUSH_TIMEOUT_MS = 8_000

/** Before a logout: flush the mail outbox when online (at most 8 s) and return
 * how many entries are still left (they are discarded with the purge). */
async function settleMailOutbox(userId: string): Promise<number> {
  const outbox = getMailOutbox(userId)
  try {
    if (onlineStatus.isOnline()) {
      let timer: ReturnType<typeof setTimeout> | undefined
      const timeout = new Promise<void>((resolve) => {
        timer = setTimeout(resolve, LOGOUT_FLUSH_TIMEOUT_MS)
      })
      await Promise.race([outbox.flush().catch(() => undefined), timeout])
      clearTimeout(timer)
    }
    return (await outbox.list()).length
  } catch {
    return 0
  }
}
